import { useEffect, useMemo, useRef, useState } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useCartContents } from '@zenbusiness/application-commons-ecommerce';
import { useExperimentTrackCallback } from '@zenbusiness/application-commons-experiments';
import useAsyncEffect from '@zenbusiness/application-commons-hooks/useAsyncEffect';
import useLocalStorage from '@zenbusiness/application-commons-hooks/useLocalStorage';
import { useIntercom } from '@zenbusiness/application-commons-intercom';
import { useError, useCaptureException } from '@zenbusiness/application-commons-logging';
import { useUpsertLead, useVisit } from '@zenbusiness/application-commons-visit';

import PAGES from '#client/config/flowPageConfig';
import { LOCAL_STORAGE_LEAD_UUID_KEY } from '#client/constants/localStorage';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import planPurchase from '#client/utils/clickstream/planPurchase';

const PAGES_TO_LD_EVENTS = {
  [PAGES.CONTACT_INFO.path]: 'lead-created-at-contact-info',
  [PAGES.CHECKOUT.path]: 'Lead Created at Order'
};

function LeadManager() {
  const {
    ready: cartIsReady,
    loading: cartIsLoading,
    items: cartItems,
    cartUuid: currentCartUuid
  } = useCartContents();
  const [trackingPlan, setTrackingPlan] = useState(planPurchase(cartItems));
  const { id: flowId, hasFormation } = useFlowConfig();
  const { businessInfo, contactInfo, trackingParams } = useFlowState();
  const { visitUuid } = useVisit();
  const { upsert: upsertLead, errors: upsertLeadErrors } = useUpsertLead({ noThrow: true });
  const { update: updateIntercom } = useIntercom();
  const isFirstRender = useRef(true);
  const [leadUuid, setLeadUuid] = useLocalStorage(LOCAL_STORAGE_LEAD_UUID_KEY, null, {
    isJSON: false
  });
  const captureException = useCaptureException();

  const cartUuid = useMemo(
    () => (hasFormation ? currentCartUuid : null),
    [hasFormation, currentCartUuid]
  );
  const trackExperimentEvent = useExperimentTrackCallback();
  const trackLeadSubmit = useClickstreamCallback('leadInfoSubmitted');
  const trackLeadCreated = useClickstreamCallback('leadCreated');
  const trackLeadUpdated = useClickstreamCallback('leadUpdated');
  const trackUserIdentified = useClickstreamCallback('userIdentified');

  /**
   * Identifies the user with the provided lead UUID and contact information.
   * Triggers the `trackUserIdentified` event to update user tracking data.
   *
   * @param {string} trackLeadUuid - The unique identifier for the lead.
   * @param {object} userContactInfo - The contact information of the user.
   * @param {string} userContactInfo.firstName - The first name of the user.
   * @param {string} userContactInfo.lastName - The last name of the user.
   * @param {string} userContactInfo.email - The email address of the user.
   * @param {string} userContactInfo.phone - The phone number of the user.
   *
   * @returns {void} - This function does not return a value.
   */
  const handleUserIdentified = (trackLeadUuid, userContactInfo) => {
    if (!trackLeadUuid || !userContactInfo?.firstName) {
      return;
    }

    const userTraits = {
      userId_: trackLeadUuid,
      leadUuid: trackLeadUuid,
      firstname: userContactInfo.firstName,
      lastname: userContactInfo.lastName,
      email: userContactInfo.email,
      phone: userContactInfo.phone
    };

    trackUserIdentified(userTraits);
  };

  /**
   * Creates or updates lead data.
   * Triggers tracking events.
   * @returns {void}
   */
  const handleLeadTrack = async () => {
    const { firstName, lastName, email, phone } = contactInfo || {};

    if (!firstName || !lastName || !email || !phone) {
      return;
    }

    const {
      businessExperience,
      businessStage,
      entityType,
      industry,
      jurisdictionState,
      name: businessName
    } = businessInfo;

    trackLeadSubmit();

    const lead = await upsertLead({
      contact: {
        firstName,
        lastName,
        email,
        phone
      },
      business: {
        name: businessName,
        type: entityType,
        jurisdictionCode: jurisdictionState,
        ...(industry && { industry }),
        ...(businessExperience && { businessExperience }),
        ...(businessStage && { businessStage })
      },
      tracking: {
        audienceType: trackingParams.audienceType || null,
        flowName: flowId,
        plan: trackingPlan,
        source: 'web'
      },
      visitUuid,
      ...(cartUuid ? { cartUuid } : {})
    });

    if (!lead) {
      return;
    }

    if (lead.uuid !== leadUuid) {
      setLeadUuid(lead.uuid);
    }

    handleUserIdentified(lead.uuid, contactInfo);
    if (lead.isNew) {
      trackLeadCreated({ dynamicProperties: { leadUuid: lead.uuid } });
      // Trigger appropriate LaunchDarkly lead created event.
      // TODO: Determine a better way to determine where the contactInfo is coming from.
      const event = PAGES_TO_LD_EVENTS[contactInfo.pageSource];
      if (event) {
        trackExperimentEvent(event);
      } else {
        captureException(
          `No LD event found for page source: ${contactInfo.pageSource} when creating Lead`,
          {
            level: 'warning'
          }
        );
      }
    } else {
      trackLeadUpdated({ dynamicProperties: { leadUuid: lead.uuid } });
    }
  };

  /**
   * Capture exceptions when upserting lead fails.
   */
  useError(upsertLeadErrors);

  /**
   * Updates the tracking plan whenever cart items change.
   */
  useEffect(() => {
    const newPlan = planPurchase(cartItems);
    if (newPlan) {
      setTrackingPlan(newPlan);
    }
  }, [cartItems]);

  // when contact info is present, identify user in intercom
  useEffect(() => {
    updateIntercom({
      ...(contactInfo.email && { email: contactInfo.email }),
      ...(contactInfo.firstName && { name: `${contactInfo.firstName} ${contactInfo.lastName}` }),
      purchase_flow: flowId
    });
  }, [contactInfo, flowId, updateIntercom]);

  // Update lead when there's a change in business info, contact info or formation plan
  useAsyncEffect(
    async () => {
      // Do not process state changes if the cart is not yet ready or is currently refreshing.
      if (!cartIsReady || cartIsLoading) {
        return undefined;
      }

      // Ignore first render.
      if (isFirstRender.current) {
        isFirstRender.current = false;
        handleUserIdentified(leadUuid, contactInfo);
        return undefined;
      }

      return handleLeadTrack();
    },
    [contactInfo, businessInfo, trackingPlan],
    { comparison: true }
  );

  return false;
}

export default function LeadManagerGate() {
  const { disableLeadManager = false } = useFlowConfig();

  if (disableLeadManager) {
    return null;
  }

  return <LeadManager />;
}
